/**
 * Adapter layer for bridging Order types between UI and Domain layers
 * Resolves API drift between OrderReadyToSubmit and CreateOrderDto
 */

import type { CreateOrderDto, OrderItemDto, DeliveryInfoDto, DietaryRequirementsDto } from '../dtos/OrderDto';
import type {
  OrderReadyToSubmit,
  CartItemReadyToSubmit,
  DeliveryData,
  DietaryData,
} from '@/components/order/order.types';
import type { CartSaved, ProductDocument, ProductVariantDocument } from '@/types/manage.types';
import { TypeHygiene } from '../utils/TypeValidation';

/**
 * Repository interfaces for product resolution
 */
export interface ProductResolutionContext {
  findVariantById(variantId: string): Promise<ProductVariantDocument | null>;
  findProductById(productId: string): Promise<ProductDocument | null>;
}

/**
 * Converts UI OrderReadyToSubmit to Domain CreateOrderDto
 * Handles type mapping and data transformation
 */
export class OrderAdapter {
  constructor(private productContext: ProductResolutionContext) {}

  /**
   * Convert OrderReadyToSubmit (UI) to CreateOrderDto (Domain)
   * Includes type validation and sanitization
   */
  async toCreateOrderDto(orderData: OrderReadyToSubmit): Promise<CreateOrderDto> {
    // Validate input data
    const validation = TypeHygiene.Validator.validateOrderReadyToSubmit(orderData);
    if (!validation.isValid) {
      throw new Error(`Invalid order data: ${validation.errors.join(', ')}`);
    }

    // Log warnings if any
    if (validation.warnings.length > 0) {
      console.warn('Order data warnings:', validation.warnings);
    }

    const items = await this.convertCartItems(orderData.cart);
    const deliveryInfo = this.convertDeliveryData(orderData.deliveryData);
    const dietaryRequirements = this.convertDietaryData(orderData.dietaryData);

    const dto: CreateOrderDto = {
      items,
      deliveryInfo,
      dietaryRequirements,
    };

    // Sanitize the final DTO
    return TypeHygiene.Sanitizer.sanitizeCreateOrderDto(dto);
  }

  /**
   * Convert CartSaved items to OrderReadyToSubmit format
   */
  fromCartItems(cartItems: CartSaved[]): CartItemReadyToSubmit[] {
    return cartItems.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
      note: item.note,
      product_name: item.product_name,
      sku: item.sku,
      in_cart: item.in_cart,
    }));
  }

  /**
   * Convert cart items with product resolution
   */
  private async convertCartItems(cartItems: CartItemReadyToSubmit[]): Promise<OrderItemDto[]> {
    const orderItems: OrderItemDto[] = [];

    for (const item of cartItems) {
      const variant = await this.productContext.findVariantById(item.variant_id);
      if (!variant) {
        throw new Error(`Variant not found: ${item.variant_id}`);
      }

      const product = await this.productContext.findProductById(variant.product_id);
      if (!product) {
        throw new Error(`Product not found: ${variant.product_id}`);
      }

      orderItems.push({
        variantId: item.variant_id,
        productId: variant.product_id,
        sku: variant.sku || '',
        productName: product.name,
        price: variant.price,
        quantity: item.quantity,
        attributes: variant.attribute_combination || [],
        note: item.note,
      });
    }

    return orderItems;
  }

  /**
   * Convert delivery data with type normalization
   */
  private convertDeliveryData(deliveryData: DeliveryData): DeliveryInfoDto {
    return {
      fullName: deliveryData.fullName,
      phoneNumber: deliveryData.phoneNumber,
      email: deliveryData.email,
      contactMethod: deliveryData.contactMethod || 'email',
      streetAddress: deliveryData.streetAddress || null,
      city: deliveryData.city,
      state: deliveryData.state,
      zipCode: deliveryData.zipCode,
      deliveryDate: deliveryData.deliveryDate || Date.now(),
      deliveryTime: deliveryData.deliveryTime || '',
    };
  }

  /**
   * Convert dietary data (same structure, ensures type compatibility)
   */
  private convertDietaryData(dietaryData: DietaryData): DietaryRequirementsDto {
    return {
      vegetarian: dietaryData.vegetarian,
      vegan: dietaryData.vegan,
      glutenFree: dietaryData.glutenFree,
      dairyFree: dietaryData.dairyFree,
      nuts: dietaryData.nuts,
      shellfish: dietaryData.shellfish,
      eggs: dietaryData.eggs,
      soy: dietaryData.soy,
      additionalComments: dietaryData.additionalComments,
    };
  }
}

/**
 * Utility functions for type compatibility
 */
export const OrderTypeUtils = {
  /**
   * Check if OrderReadyToSubmit has all required fields
   */
  isValidForSubmission(order: OrderReadyToSubmit): boolean {
    return !!(
      order.cart.length > 0 &&
      order.deliveryData.fullName &&
      order.deliveryData.email &&
      order.deliveryData.phoneNumber &&
      order.deliveryData.city &&
      order.deliveryData.state &&
      order.deliveryData.zipCode
    );
  },

  /**
   * Convert DeliveryFormData to DeliveryData (for form compatibility)
   */
  normalizeDeliveryData(formData: Record<string, unknown>): DeliveryData {
    return {
      fullName: (formData.fullName as string) || '',
      phoneNumber: (formData.phoneNumber as string) || '',
      email: (formData.email as string) || '',
      contactMethod: (formData.contactMethod === 'email' || formData.contactMethod === 'phone'
        ? formData.contactMethod
        : 'email') as 'email' | 'phone',
      streetAddress: (formData.streetAddress as string) || undefined,
      city: (formData.city as string) || '',
      state: (formData.state as string) || '',
      zipCode: (formData.zipCode as string) || '',
      deliveryDate:
        typeof formData.deliveryDate === 'string'
          ? parseInt(formData.deliveryDate) || Date.now()
          : (formData.deliveryDate as number) || Date.now(),
      deliveryTime: (formData.deliveryTime as string) || '',
    };
  },
};
